import React, { useEffect, useRef, useState } from 'react';
import styles from './GetReadyModal.module.scss';
import { CustomButton, CustomCheckbox } from 'shared_ui_components';
import RoleplayInfo from './components/RoleplayInfo';

function GetReadyModal({
  isOpen = false,
  onClose = () => {},
  mode = 'practice',
  scenario = '',
  time = '',
  contextDescription = '',
  tips = [],
}) {
  const [isChecked, setIsChecked] = useState(false);
  const modalRef = useRef(null);
  const focusedElementIndex = useRef(0);

  useEffect(() => {
    const handleTabKey = (e) => {
      const focusableModalElements = modalRef?.current?.querySelectorAll(
        "a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex='-1'])"
      );

      if (!e.shiftKey) {
        focusableModalElements?.[focusedElementIndex?.current]?.focus?.();
        if (focusedElementIndex?.current >= focusableModalElements?.length - 1) {
          focusedElementIndex.current = 0;
        } else {
          focusedElementIndex.current += 1;
        }
        return e.preventDefault();
      }

      if (e.shiftKey) {
        focusableModalElements?.[focusedElementIndex?.current]?.focus?.();
        if (focusedElementIndex?.current <= 0) {
          focusedElementIndex.current = focusableModalElements?.length - 1;
        } else {
          focusedElementIndex.current -= 1;
        }
        e.preventDefault();
      }
    };

    const handleKeyDown = (e) => {
      //if (e.key === 'Escape') onClose();
      if (e.key === 'Tab') handleTabKey(e);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      focusedElementIndex.current = 0;
    };
  }, []);

  return (
    isOpen && (
      <div
        className={`${styles.centerAlignment} ${styles.modalOverlay}`}
        role="dialog"
        aria-modal="true"
      >
        <div ref={modalRef} className={styles.modalContainer}>
          <h1 className={styles.modalTitle}>Get Ready!</h1>
          <div className={styles.modalBody}>
            <div className={styles.header}>
              {mode === 'practice' ? (
                <svg
                  className={styles.headerIcon}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 640 640"
                >
                  <path d="M416.9 85.2L372 130.1L509.9 268L554.8 223.1C568.4 209.6 576 191.2 576 172C576 152.8 568.4 134.4 554.8 120.9L519.1 85.2C505.6 71.6 487.2 64 468 64C448.8 64 430.4 71.6 416.9 85.2zM338.1 164L122.9 379.1C112.2 389.8 104.4 403.2 100.3 417.8L64.9 545.6C62.6 553.9 64.9 562.9 71.1 569C77.3 575.1 86.2 577.5 94.5 575.2L222.3 539.7C236.9 535.6 250.2 527.9 261 517.1L476 301.9L338.1 164z" />
                </svg>
              ) : (
                <svg
                  className={styles.headerIcon}
                  viewBox="0 0 48 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M22.25 25L20 19.6L17.75 25H22.25Z" />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M38 38C38 40.2091 36.2091 42 34 42H14C11.7909 42 10 40.2091 10 38V10C10 7.79086 11.7909 6 14 6H28L38 16V38ZM29 9.82843L34.1716 15H29V9.82843ZM20.9231 16.6154C20.7678 16.2427 20.4037 16 20 16C19.5963 16 19.2322 16.2427 19.0769 16.6154L14.0769 28.6154C13.8645 29.1252 14.1056 29.7107 14.6154 29.9231C15.1252 30.1355 15.7107 29.8944 15.9231 29.3846L16.9167 27H23.0833L24.0769 29.3846C24.2893 29.8944 24.8748 30.1355 25.3846 29.9231C25.8944 29.7107 26.1355 29.1252 25.9231 28.6154L20.9231 16.6154ZM15 32C14.4477 32 14 32.4477 14 33C14 33.5523 14.4477 34 15 34L33 34C33.5523 34 34 33.5523 34 33C34 32.4477 33.5523 32 33 32L15 32ZM14 37C14 36.4477 14.4477 36 15 36H27C27.5523 36 28 36.4477 28 37C28 37.5523 27.5523 38 27 38H15C14.4477 38 14 37.5523 14 37ZM30 22C30.5523 22 31 22.4477 31 23V25H33C33.5523 25 34 25.4477 34 26C34 26.5523 33.5523 27 33 27H31V29C31 29.5523 30.5523 30 30 30C29.4477 30 29 29.5523 29 29V27H27C26.4477 27 26 26.5523 26 26C26 25.4477 26.4477 25 27 25H29V23C29 22.4477 29.4477 22 30 22Z"
                  />
                </svg>
              )}

              <h2 className={styles.roleplayMode}>
                Mode: <span>{mode}</span>
              </h2>
            </div>
            <p className={`${styles.roleplayDesc} ${styles[mode]}`}>{contextDescription}</p>
            <RoleplayInfo title="Scenario:" info={scenario} />
            <RoleplayInfo title="Time:" info={`Aprox. ${time}`} />
            <RoleplayInfo title="Skill Focus:" info="Reframing Value + Next Steps" />
            {tips?.length > 0 && (
              <div className={styles.tipsContainer}>
                <div className={styles.header} style={{ marginBottom: '16px' }}>
                  <svg
                    className={styles.tipIcon}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 640 640"
                  >
                    <path d="M420.9 448C428.2 425.7 442.8 405.5 459.3 388.1C492 353.7 512 307.2 512 256C512 150 426 64 320 64C214 64 128 150 128 256C128 307.2 148 353.7 180.7 388.1C197.2 405.5 211.9 425.7 219.1 448L420.8 448zM416 496L224 496L224 512C224 556.2 259.8 592 304 592L336 592C380.2 592 416 556.2 416 512L416 496zM312 176C272.2 176 240 208.2 240 248C240 261.3 229.3 272 216 272C202.7 272 192 261.3 192 248C192 181.7 245.7 128 312 128C325.3 128 336 138.7 336 152C336 165.3 325.3 176 312 176z" />
                  </svg>
                  <h3>Tips for Mastering This Roleplay</h3>
                </div>
                <ul>
                  {tips.map((tip, index) => <li key={index}>{tip}</li>)}
                </ul>
              </div>
            )}
            <div className={styles.checkboxContainer}>
              <CustomCheckbox
                label="I've read the brief and I'm ready to sell this like a real deal."
                type="secondary"
                value={isChecked}
                onChange={(value) => setIsChecked(value)}
              />
            </div>
          </div>
          <div className={styles.btnContainer}>
            <CustomButton onClick={onClose} disabled={!isChecked}>
              Start Roleplay
            </CustomButton>
          </div>
        </div>
      </div>
    )
  );
}

export default GetReadyModal;
